"use client";
import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { IconButton, Stack, useTheme } from "@mui/material";
import { BaseMobileDrawerProps } from "./MobileDrawer";
import { BaseDrawerProps } from "./LeftDrawer";
import { BaseAppBarProps } from "./AppBar";
import { Drawer } from "./Drawer";
import { DrawerHeader } from "./DrawerHeader";
import { ChevronLeftOutlined, ChevronRightOutlined } from "@mui/icons-material";

const RIGHT_CONTAINER_DEFAULT_WIDTH = "25%";

const TopSpacer = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

export interface AppContainerV2Props {
  children: JSX.Element | JSX.Element[];
  /**
   * Set this property to true if drawer should be under app bar
   */
  clippedDrawer: boolean;
  drawerWidth: number;
  containerBackgroundColor?: string;

  /* 
  Set this property to true if the right container should be below the app bar
  */
  clippedRightContainer?: boolean;
  /*
  Pass a JSX.Element to be located next to the main container
  */
  rightContainer?: JSX.Element;
  rightContainerWidth?: number | string;
  AppBar: (props: BaseAppBarProps) => JSX.Element;
  Drawer: (props: BaseDrawerProps) => JSX.Element;
  MobileDrawer: (props: BaseMobileDrawerProps) => JSX.Element;
  hideTopSpacer: boolean;
}

AppContainerV2.defaultProps = {
  drawerWidth: 240,
  containerBackgroundColor: "white",
  hideTopSpacer: false,
};

export function AppContainerV2(props: AppContainerV2Props) {
  const theme = useTheme();

  const [open, setOpen] = React.useState(true);
  const [isOpenRightDrawer, setIsOpenRightDrawer] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleRightDrawerClose = () => {
    setIsOpenRightDrawer(false);
  };

  const handleRightDrawerOpen = () => {
    setIsOpenRightDrawer(true);
  };

  const handleRightDrawerToggle = () => {
    setIsOpenRightDrawer(!isOpenRightDrawer);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileDrawerClose = () => {
    setMobileOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        backgroundColor: props.containerBackgroundColor,
      }}
    >
      <CssBaseline />

      <props.AppBar
        open={open}
        clippedDrawer={props.clippedDrawer}
        onClickMenuButton={handleDrawerToggle}
        onClickMobileMenuButton={handleMobileDrawerToggle}
        drawerWidth={props.drawerWidth}
      />

      <props.MobileDrawer
        open={mobileOpen}
        clippedDrawer={props.clippedDrawer}
        onClose={handleMobileDrawerClose}
        width={props.drawerWidth}
      />

      <props.Drawer
        open={open}
        clippedDrawer={props.clippedDrawer}
        onClose={handleDrawerClose}
        onClickMenuButton={handleDrawerToggle}
        onClickMobileMenuButton={handleMobileDrawerToggle}
        onOpen={handleDrawerOpen}
        width={props.drawerWidth}
      />

      <Stack
        sx={{
          width: props.rightContainer
            ? props.rightContainerWidth
              ? `calc(100% - ${props.rightContainerWidth} - ${
                  open ? props.drawerWidth : 0
                }px)`
              : `calc(100% - ${RIGHT_CONTAINER_DEFAULT_WIDTH} - ${
                  open ? props.drawerWidth : 0
                }px)`
            : "100%",
          [theme.breakpoints.down("md")]: {
            width: "100%",
          },
        }}
      >
        {!props.hideTopSpacer && <TopSpacer />}
        {props.children}
      </Stack>

      {/* Uses a permanent Drawer to wrap the object you want to pass on */}
      {props.rightContainer && (
        <Drawer
          hasFooter={false}
          clippedDrawer={props.clippedRightContainer}
          width={props.rightContainerWidth || RIGHT_CONTAINER_DEFAULT_WIDTH}
          variant="permanent"
          anchor={"right"}
          open={isOpenRightDrawer}
          sx={{
            flexShrink: 0,
            whiteSpace: "nowrap",
            boxSizing: "border-box",
            ...(props.clippedRightContainer
              ? {}
              : {
                  zIndex: theme.zIndex.drawer + 2,
                }),
          }}
        >
          {props.rightContainer}
        </Drawer>
      )}
    </Box>
  );
}
